import {AsyncPipe} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConsultationPopupRibbonService} from '@services/core/consultation-popup/consultation-popup-ribbon.service';
import {AppAuthService} from '@services/core/auth/app-auth.service';
import {UserCapabilitiesService} from '@services/core/capabilities/user-capabilities.service';
import {IRouteStructure} from '@shared/common/routing/IRouteStructure';
import {ClinicianDashboardRoutes} from '@shared/common/routing/clinician-dashboard-routes';
import {SidebarComponent} from '@shared/modules/layout-components/sidebar/sidebar.component';
import {HeaderComponent} from '@shared/modules/layout-components/header/header.component';
import {CFTranslatorUtil} from '@shared/utils/code-feature-translation/CFTranslator.util';
import {IconMap} from '@shared/common/icons';
import {
  AppointmentRibbonComponent,
} from '@app/areas/appointments/components/appointment-ribbon/appointment-ribbon.component';
import {ChatSidebarBadgeService} from '@app/areas/chat/services/chat-sidebar-badge.service';
import {EnquiriesSidebarBadgeService} from '../areas/crm/enquiries/services/enquiries-sidebar-badge.service';
import {WorkspaceSwitcherService} from '@services/core/workspace-switcher/workspace-switcher.service';
import {
  ADMIN_NAVIGATION_ITEMS,
  AdminNavigationItem,
  getAccessibleAdminNavigationItems,
  getFirstAccessibleAdminNavigationItem,
  isAdminNavigationUrl,
} from './admin-navigation';
import {
  getAccessibleSettingsNavigationItems,
  getFirstAccessibleSettingsNavigationItem,
  isSettingsNavigationUrl,
  SETTINGS_NAVIGATION_ITEMS,
} from './settings-navigation';

@Component({
  selector: 'app-clinician-dashboard-index',
  templateUrl: './clinician-dashboard-index.component.html',
  styleUrls: ['./clinician-dashboard-index.component.scss'],
  standalone: true,
  imports: [HeaderComponent, SidebarComponent, RouterOutlet, AsyncPipe, AppointmentRibbonComponent],
})
export class ClinicianDashboardIndexComponent implements OnInit, OnDestroy {
  private readonly unsub = new Subject<void>();
  private currentUrl: string;

  private readonly basePrimarySidebarItems: IRouteStructure[] = [
    {
      type: 'url',
      localPath: '',
      fullUrl: ClinicianDashboardRoutes.PATIENT_LIST.list?.fullUrl ?? '/cn/patient-list',
      localeKey: ClinicianDashboardRoutes.PATIENT_LIST.list?.localeKey ?? 'Patients',
      translationDefault: ClinicianDashboardRoutes.PATIENT_LIST.list?.translationDefault ?? 'Patients',
      icon: ClinicianDashboardRoutes.PATIENT_LIST.list?.icon ?? IconMap.accountGroupOutline,
    },
    {
      type: 'url',
      localPath: '',
      fullUrl: ClinicianDashboardRoutes.CONSULTS.list?.fullUrl ?? '/cn/consults',
      localeKey: ClinicianDashboardRoutes.CONSULTS.list?.localeKey ?? 'Consults',
      translationDefault: ClinicianDashboardRoutes.CONSULTS.list?.translationDefault ?? 'Consults',
      icon: ClinicianDashboardRoutes.CONSULTS.list?.icon ?? IconMap.calendar,
    },
    {
      type: 'url',
      localPath: '',
      fullUrl: `/${ClinicianDashboardRoutes.DASHBOARD_PREFIX}/${CFTranslatorUtil.Prescriptions.snakeCase}`,
      localeKey: CFTranslatorUtil.Builder.default,
      translationDefault: 'Prescription\nBuilder',
      icon: IconMap.medicalTx,
    },
  ];

  private readonly chatSidebarBadge = inject(ChatSidebarBadgeService);
  private readonly enquiriesSidebarBadge = inject(EnquiriesSidebarBadgeService);
  private readonly capabilitiesService = inject(UserCapabilitiesService);

  constructor(
    public consultationPopup: ConsultationPopupRibbonService,
    private readonly authService: AppAuthService,
    private readonly router: Router,
    public readonly workspaceSwitcher: WorkspaceSwitcherService,
  ) {
    this.currentUrl = this.router.url;

    this.router.events
      .pipe(takeUntil(this.unsub))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.currentUrl = event.urlAfterRedirects;
        }
      });
  }

  ngOnInit(): void {
    void this.chatSidebarBadge.refresh();

    if (this.authService.hasRole('customer-support')) {
      void this.enquiriesSidebarBadge.refresh();
    }
  }

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }

  get primarySidebarItems(): IRouteStructure[] {
    const items = [
      this.basePrimarySidebarItems[0],
      this.basePrimarySidebarItems[1],
      this.buildChatItem(),
    ];

    if (this.capabilitiesService.hasCapabilitySignal('prescription_builder:create')() ||
        this.capabilitiesService.hasCapabilitySignal('prescription_builder:view')()) {
      items.push(this.basePrimarySidebarItems[2]);
    }

    if (this.authService.hasRole('customer-support')) {
      items.push(this.buildSupportItem());
    }

    return items;
  }

  get adminSidebarItems(): AdminNavigationItem[] {
    return getAccessibleAdminNavigationItems(role => this.authService.hasRole(role));
  }

  get adminEntryPoint(): IRouteStructure | null {
    const item = getFirstAccessibleAdminNavigationItem(role => this.authService.hasRole(role));

    return item
      ? {
          ...item,
          icon: 'mdi-security',
        }
      : null;
  }

  get settingsSidebarItems(): IRouteStructure[] {
    const items = [...getAccessibleSettingsNavigationItems()];
    if (this.workspaceSwitcher.isLegalEntityOwner$()) {
      items.push({
        ...ClinicianDashboardRoutes.ORGANISATION_SETTINGS.view,
        translationDefault: 'Organisation settings',
      });
    }
    return items;
  }

  get settingsEntryPoint(): IRouteStructure | null {
    return getFirstAccessibleSettingsNavigationItem();
  }

  get footerActions(): IRouteStructure[] {
    return [
      ...(this.adminEntryPoint
        ? [{
            ...this.adminEntryPoint,
            translationDefault: 'Admin',
          }]
        : []),
      this.buildFeedbackItem(),
      ...(this.settingsEntryPoint
        ? [{
            ...this.settingsEntryPoint,
            translationDefault: 'Settings',
            icon: IconMap.settings,
          }]
        : []),
    ];
  }

  get showAdminRail(): boolean {
    return isAdminNavigationUrl(this.currentUrl);
  }

  get showSettingsRail(): boolean {
    return isSettingsNavigationUrl(this.currentUrl);
  }

  get primarySidebarInactivePrefixes(): string[] {
    return [
      ...ADMIN_NAVIGATION_ITEMS.map(item => item.fullUrl as string),
      ...SETTINGS_NAVIGATION_ITEMS.map(item => item.fullUrl as string),
    ];
  }

  private buildSupportItem(): IRouteStructure {
    return {
      type: 'url',
      localPath: '',
      fullUrl: `/cn/support`,
      localeKey: 'Support',
      translationDefault: 'Support',
      icon: 'mdi-information-outline',
      badgeCount: this.enquiriesSidebarBadge.count(),
    };
  }

  private buildChatItem(): IRouteStructure {
    return {
      type: 'url',
      localPath: '',
      fullUrl: ClinicianDashboardRoutes.CHAT.list?.fullUrl ?? '/cn/chat',
      localeKey: ClinicianDashboardRoutes.CHAT.list?.localeKey ?? 'Chat',
      translationDefault: ClinicianDashboardRoutes.CHAT.list?.translationDefault ?? 'Chat',
      icon: ClinicianDashboardRoutes.CHAT.list?.icon ?? IconMap.chat,
      badgeCount: this.chatSidebarBadge.count(),
    };
  }

  private buildFeedbackItem(): IRouteStructure {
    return {
      type: 'url',
      localPath: '',
      fullUrl: ClinicianDashboardRoutes.FEEDBACK.view?.fullUrl ?? '/cn/feedback',
      localeKey: ClinicianDashboardRoutes.FEEDBACK.view?.localeKey ?? 'Feedback',
      translationDefault: ClinicianDashboardRoutes.FEEDBACK.view?.translationDefault ?? 'Feedback',
      icon: ClinicianDashboardRoutes.FEEDBACK.view?.icon ?? IconMap.clipboardEditOutline,
    };
  }
}
